import { journeys as journeysApi, segments as segmentsApi, attributes as attributesApi } from '../../../apiService/blustreamApi';
import { adaptJourney, adaptTouchpoint, adaptSegment, buildAttributeIndex } from './adapt';
import { fetchAll, resolveTimelineSource } from './useJourneyData';
import { journeys as fixtureJourneys } from '../fixtures/journeys';
import { segments as fixtureSegments } from '../fixtures/segments';
import { attributeById as fixtureAttributeById } from '../fixtures/attributes';

const PAGE = 200;

// Real-API source. Takes the api module so it can be unit-tested with mocks.
export function apiJourneySource(api = { journeys: journeysApi, segments: segmentsApi, attributes: attributesApi }) {
  return {
    async listJourneys(signal) {
      const raw = await fetchAll((page) => api.journeys.list({ page, size: PAGE, signal }));
      return raw.map((j) => adaptJourney(j, [])); // list endpoint has no touchpoints
    },
    async listTouchpoints(journeyId, signal) {
      const raw = await fetchAll((page) => api.journeys.listTouchpoints(journeyId, { page, size: PAGE, signal }));
      return raw.map(adaptTouchpoint);
    },
    async attributeIndex(signal) {
      const attrs = await fetchAll((page) => api.attributes.list({ page, size: PAGE, signal }));
      return buildAttributeIndex(attrs);
    },
    async segmentsFor(segmentIds, signal) {
      const byId = new Map();
      const inFlight = new Map(); // id -> Promise, so parallel calls dedupe
      const fetchSeg = (id) => {
        if (id == null || inFlight.has(id)) return inFlight.get(id) || Promise.resolve();
        const p = (async () => {
          const seg = adaptSegment(await api.segments.get(id, { signal }));
          byId.set(id, seg);
          if (seg && seg.parent_segment_id != null) await fetchSeg(seg.parent_segment_id);
        })();
        inFlight.set(id, p);
        return p;
      };
      await Promise.all([...new Set(segmentIds.filter((id) => id != null))].map(fetchSeg));
      return [...byId.values()];
    },
  };
}

// Fixture source: same interface, resolved immediately from in-memory fixtures.
// listJourneys strips touchpoints so the progressive path (listTouchpoints) is
// exercised identically to the API path in /preview and tests.
export function fixtureJourneySource() {
  const tpByJourney = new Map(fixtureJourneys.map((j) => [j.journey_id, j.touchpoints || []]));
  return {
    listJourneys: async () => fixtureJourneys.map((j) => ({ ...j, touchpoints: [] })),
    listTouchpoints: async (journeyId) => (tpByJourney.get(journeyId) || []).map(adaptTouchpoint),
    attributeIndex: async () => fixtureAttributeById,
    segmentsFor: async (segmentIds) => {
      const byId = new Map(fixtureSegments.map((s) => [s.segment_id, s]));
      const out = new Map();
      const add = (id) => {
        if (id == null || out.has(id)) return;
        const s = byId.get(id);
        if (!s) return;
        out.set(id, s);
        if (s.parent_segment_id != null) add(s.parent_segment_id);
      };
      [...new Set(segmentIds.filter((id) => id != null))].forEach(add);
      return [...out.values()];
    },
  };
}

export function getJourneySource() {
  return resolveTimelineSource() === 'fixtures' ? fixtureJourneySource() : apiJourneySource();
}
